import React, { useState, useMemo } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, elevation, formatMoney } from '../shared/tokens';
import sampleData from '../shared/sampleData';

import RelationshipMap from '../shared/components/RelationshipMap';
import RelationshipCardList from '../shared/components/RelationshipCardList';
import RelationshipFilterBar from '../shared/components/RelationshipFilterBar';
import SectionCard from '../shared/components/SectionCard';
import Avatar from '../shared/components/Avatar';

// ───────────────────────────────────────────────────────────────────────────
// Build graph nodes & edges from sample data
// ───────────────────────────────────────────────────────────────────────────

const buildGraph = (rootId) => {
  const root = Object.values(sampleData.contacts).find(c => c.id === rootId);
  const nodes = [];
  const edges = [];

  // Root
  nodes.push({
    id: root.id,
    label: `${root.firstName} ${root.lastName}`,
    subtitle: root.title || 'Primary contact',
    initials: root.avatarInitials,
    avatarColor: root.avatarColor,
    isRoot: true,
  });

  // Household members (other than root)
  const household = sampleData.household;
  household.memberIds.forEach(memId => {
    if (memId === rootId) return;
    const m = Object.values(sampleData.contacts).find(c => c.id === memId);
    if (!m) return;
    nodes.push({
      id: m.id,
      label: `${m.firstName} ${m.lastName}`,
      subtitle: household.roles[m.id],
      initials: m.avatarInitials,
      avatarColor: m.avatarColor,
      _giving: m.lifetimeGiving,
    });
    edges.push({
      id: `hh-${m.id}`,
      from: rootId,
      to: m.id,
      label: 'Household',
      category: 'household',
    });
  });

  // Explicit Relationships (not already covered by household)
  sampleData.relationships.forEach(r => {
    if (r.fromId !== rootId) return;
    if (household.memberIds.includes(r.toId)) {
      // Relabel the existing household edge with the actual relationship type
      const existing = edges.find(e => e.to === r.toId);
      if (existing) existing.label = r.type;
      return;
    }
    const c = Object.values(sampleData.contacts).find(x => x.id === r.toId);
    if (!c) return;
    nodes.push({
      id: c.id,
      label: `${c.firstName} ${c.lastName}`,
      subtitle: r.type,
      initials: c.avatarInitials,
      avatarColor: c.avatarColor,
    });
    const category = ['Spouse', 'Parent', 'Child', 'Sibling'].includes(r.type)
      ? 'family'
      : 'friend';
    edges.push({
      id: r.id,
      from: rootId,
      to: c.id,
      label: r.type,
      category,
      former: r.status === 'Former',
    });
  });

  // Affiliations
  sampleData.affiliations.forEach(a => {
    if (a.contactId !== rootId) return;
    const org = Object.values(sampleData.organizations).find(o => o.id === a.organizationId);
    if (!org) return;
    const orgNodeId = `${org.id}__${a.id}`;
    if (!nodes.find(n => n.id === orgNodeId)) {
      nodes.push({
        id: orgNodeId,
        label: org.name,
        subtitle: org.industry,
        initials: org.name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
        avatarColor: colors.moves.solicit,
      });
    }
    edges.push({
      id: `aff-${a.id}`,
      from: rootId,
      to: orgNodeId,
      label: a.role,
      category: a.role.toLowerCase().includes('board') || a.role.toLowerCase().includes('treasurer')
        ? 'board'
        : 'affiliation',
      former: a.status === 'Former',
    });
  });

  // Soft credits → connect to donors who gave with root's influence
  sampleData.softCredits.slice(0, 2).forEach(sc => {
    if (sc.contactId !== rootId) return;
    // The donorName is a free-text stand-in; add a node for the source
    const scNodeId = `sc-${sc.id}`;
    if (!nodes.find(n => n.id === scNodeId)) {
      nodes.push({
        id: scNodeId,
        label: sc.donorName,
        subtitle: `${sc.role} gift`,
        initials: sc.donorName.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
        avatarColor: colors.info,
      });
    }
    edges.push({
      id: `sc-edge-${sc.id}`,
      from: rootId,
      to: scNodeId,
      label: formatMoney(sc.amount, { compact: true }),
      category: 'softCredit',
    });
  });

  return { nodes, edges };
};

// ───────────────────────────────────────────────────────────────────────────
// Group the same data into the card-list fallback
// ───────────────────────────────────────────────────────────────────────────

const buildCardGroups = (rootId) => {
  const household = sampleData.household;
  const householdItems = household.memberIds
    .filter(id => id !== rootId)
    .map(id => {
      const c = Object.values(sampleData.contacts).find(x => x.id === id);
      if (!c) return null;
      const rel = sampleData.relationships.find(r => r.fromId === rootId && r.toId === id);
      return {
        id: c.id,
        label: `${c.firstName} ${c.lastName}`,
        subtitle: `${rel?.type || household.roles[c.id]} · Household`,
        initials: c.avatarInitials,
        avatarColor: c.avatarColor,
        statLabel: 'Lifetime',
        statValue: formatMoney(c.lifetimeGiving || 0, { compact: true }),
      };
    })
    .filter(Boolean);

  const affiliationItems = sampleData.affiliations
    .filter(a => a.contactId === rootId)
    .map(a => {
      const org = Object.values(sampleData.organizations).find(o => o.id === a.organizationId);
      if (!org) return null;
      return {
        id: `aff-${a.id}`,
        label: org.name,
        subtitle: `${a.role} · ${a.status}`,
        initials: org.name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
        avatarColor: colors.moves.solicit,
        former: a.status === 'Former',
        statLabel: 'Matching',
        statValue: org.matchingGiftCompany ? '✓' : '—',
      };
    })
    .filter(Boolean);

  const relationshipItems = sampleData.relationships
    .filter(r => r.fromId === rootId && !household.memberIds.includes(r.toId))
    .map(r => {
      const c = Object.values(sampleData.contacts).find(x => x.id === r.toId);
      if (!c) return null;
      return {
        id: c.id,
        label: `${c.firstName} ${c.lastName}`,
        subtitle: `${r.type} · ${r.status}`,
        initials: c.avatarInitials,
        avatarColor: c.avatarColor,
      };
    })
    .filter(Boolean);

  const softCreditItems = sampleData.softCredits
    .filter(sc => sc.contactId === rootId)
    .map(sc => ({
      id: `sc-${sc.id}`,
      label: sc.donorName,
      subtitle: `${sc.role} gift`,
      initials: sc.donorName.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
      avatarColor: colors.info,
      statLabel: 'Amount',
      statValue: formatMoney(sc.amount, { compact: true }),
    }));

  return {
    household: householdItems,
    affiliations: affiliationItems,
    relationships: relationshipItems,
    softCredits: softCreditItems,
  };
};

// ───────────────────────────────────────────────────────────────────────────
// Detail pane
// ───────────────────────────────────────────────────────────────────────────

const DetailPane = ({ selectedId, nodes, edges, rootId, onClear }) => {
  if (!selectedId || selectedId === rootId) {
    return (
      <div style={{
        padding: space[5],
        background: colors.surfaceInk,
        borderRadius: radius.md,
        border: `1px dashed ${colors.border}`,
        color: colors.textTertiary,
        ...type.small,
        textAlign: 'center',
      }}>
        <Icon category="utility" name="touch_action" size="small" style={{ fill: colors.textTertiary, marginBottom: space[2] }} />
        <div>Click a node or card to see relationship detail.</div>
      </div>
    );
  }
  const node = nodes.find(n => n.id === selectedId);
  const edge = edges.find(e => e.to === selectedId || e.from === selectedId);
  const root = nodes.find(n => n.id === rootId);
  if (!node) return null;

  // Simulated "giving together" stat
  const givingTogether = node._giving ? node._giving + (root?._giving || 0) : null;

  return (
    <div style={{
      padding: space[4],
      background: colors.surface,
      borderRadius: radius.md,
      border: `1px solid ${colors.border}`,
      boxShadow: elevation.card,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: space[3] }}>
        <div style={{ ...type.label, color: colors.textTertiary }}>Relationship detail</div>
        <button
          onClick={onClear}
          title="Close"
          style={{
            padding: 0, background: 'transparent', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
          }}
        >
          <Icon category="utility" name="close" size="x-small" style={{ fill: colors.textTertiary }} />
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: space[3], marginBottom: space[3] }}>
        <Avatar initials={node.initials} color={node.avatarColor} size="lg" />
        <div>
          <div style={{ ...type.h3, color: colors.textPrimary }}>{node.label}</div>
          <div style={{ ...type.small, color: colors.textTertiary, marginTop: 2 }}>{node.subtitle}</div>
        </div>
      </div>
      {edge && (
        <div style={{
          padding: space[3],
          background: colors.surfaceInk,
          borderRadius: radius.sm,
          marginBottom: space[3],
          ...type.small,
        }}>
          <div style={{ ...type.label, color: colors.textTertiary, marginBottom: 4 }}>Connection</div>
          <div style={{ color: colors.textPrimary, fontWeight: 600 }}>
            {edge.label} {edge.former && <span style={{ color: colors.warning }}>(former)</span>}
          </div>
        </div>
      )}
      {givingTogether != null && (
        <div style={{
          padding: space[3],
          background: `${colors.timeline.gift}11`,
          borderRadius: radius.sm,
          marginBottom: space[3],
        }}>
          <div style={{ ...type.label, color: colors.textTertiary, marginBottom: 4 }}>
            Giving together
          </div>
          <div style={{ ...type.kpi, color: colors.timeline.gift }}>
            {formatMoney(givingTogether, { compact: true })}
          </div>
        </div>
      )}
      <button style={{
        width: '100%',
        padding: `${space[2]}px ${space[3]}px`,
        background: colors.brand,
        color: '#fff',
        border: 'none',
        borderRadius: radius.sm,
        ...type.small,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}>
        Go to record
      </button>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────────────────────

const RelationshipExplorer = () => {
  const rootId = sampleData.contacts.eleanor.id;
  const root = sampleData.contacts.eleanor;
  const { nodes, edges } = useMemo(() => buildGraph(rootId), [rootId]);
  const groups = useMemo(() => buildCardGroups(rootId), [rootId]);

  const [filter, setFilter] = useState(null); // null = all
  const [selectedId, setSelectedId] = useState(null);

  const edgeCount = edges.length;

  return (
    <div style={{ minHeight: '100%', background: colors.surfaceInk }}>
      {/* Hero */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${space[4]}px ${space[6]}px`,
        boxShadow: elevation.sticky,
      }}>
        <div style={{ display: 'flex', gap: space[4], alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            width: 64, height: 64, borderRadius: radius.lg,
            background: `${colors.moves.cult}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon category="standard" name="relationship" size="medium" style={{ fill: colors.moves.cult }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ ...type.h1, color: colors.textPrimary, margin: 0 }}>
              Relationships for {root.firstName} {root.lastName}
            </h1>
            <div style={{ ...type.small, color: colors.textTertiary, marginTop: 2 }}>
              {edgeCount} connections · household · affiliations · soft-credit network
            </div>
          </div>
        </div>
        <div style={{ marginTop: space[4] }}>
          <RelationshipFilterBar active={filter} onChange={setFilter} />
        </div>
      </header>

      <div style={{ padding: space[5] }}>
        <div style={{ display: 'flex', gap: space[4], flexWrap: 'wrap', marginBottom: space[4] }}>
          {/* Graph */}
          <div style={{ flex: '1 1 600px', minWidth: 0 }}>
            <SectionCard
              title="Graph view"
              subtitle="Dagre-laid-out · click a node for detail"
              padded={false}
            >
              <div style={{ padding: space[2] }}>
                <RelationshipMap
                  nodes={nodes}
                  edges={edges}
                  activeEdgeTypes={filter}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              </div>
            </SectionCard>
          </div>
          {/* Detail pane */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <DetailPane
              selectedId={selectedId}
              nodes={nodes}
              edges={edges}
              rootId={rootId}
              onClear={() => setSelectedId(null)}
            />
          </div>
        </div>

        {/* Card list fallback — always shown; for narrow viewports it stands alone */}
        <SectionCard
          title="All connections"
          subtitle="Grouped by type · always rendered as an accessible fallback"
        >
          <RelationshipCardList
            groups={groups}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </SectionCard>
      </div>
    </div>
  );
};

export default RelationshipExplorer;
